import { useState } from 'react';
import { AIRCRAFT_ROLES, type AssetCounts, type LayerPrefs } from './prefs';
import {
  LAYER_GROUPS,
  radioSiblings,
  type BooleanPrefKey,
  type GroupId,
  type LayerDef,
  type LayerGroupDef,
} from './layerGroups';
import { OC_CATEGORY_ICON, OC_CATEGORY_LABEL, OC_COLOR, renderLucideToDataUrl } from './iconRegistry';
import { RESPONSE_LEVEL_COLORS, THREAT_LEVEL_META } from './styles/responseLevel';
import { areaCommandLayerLabel } from './incidentVocabulary';
import type { OntarioCorpsPartner } from './api';
import { logAction } from './lib/operatorActions';
import { MutedChip } from './MutedChip';
import { FeedDownChip } from './FeedDownChip';
import type { RoadClosureCoverageState } from './roadClosureCoverage';
import {
  ROAD_CLOSURE_COVERAGE_NOTE,
  roadClosureCoverageCue,
} from './roadClosureCoverageVocabulary';
import type { LayerFeedDown, LayerKey } from '@tsr/shared-types';

/** Compact boolean snapshot of the current layer toggles — the COP context an
 *  audit row needs to know what was visible when the operator flipped one. */
function toggleSnapshot(p: LayerPrefs): Record<string, boolean> {
  return {
    cameras: p.cameras,
    smokePriorityOnly: p.smokePriorityOnly,
    urgentAnomalies: p.urgentAnomalies,
    extremeWeatherWarnings: p.extremeWeatherWarnings,
    liveAircraft: p.liveAircraft,
    reportedOcWildfires: p.reportedOcWildfires,
    allReportedWildfires: p.allReportedWildfires,
    fwiHeatmap: p.fwiHeatmap,
    aviationBases: p.aviationBases,
    fireStations: p.fireStations,
    wildfireBases: p.wildfireBases,
    ontarioCorps: p.ontarioCorps,
    sensors: p.sensors,
    provincialBoundaries: p.provincialBoundaries,
  };
}

interface LayerToggleProps {
  prefs: LayerPrefs;
  onChange: (next: LayerPrefs) => void;
  cameraCount: number;
  sensorCount: number;
  assetCounts: AssetCounts;
  aircraftCount: number;
  /** Active wildland fires currently in view (Reported OC / All Wildfires). */
  fireCount: number;
  /** #424 — in-view URGENT reds (RL≥3) across ALL anomaly sources (hotspot + CAP +
   *  flood + ice), the SAME value as the rail "Alerts" badge so the two counts on the
   *  "Urgent Alerts" surface never disagree. Was highRiskCount (fire-hotspots only,
   *  which under-reported CAP/flood/ice).
   *  #325 — `null` = UNKNOWN (API unreachable): the row's count blanks rather than
   *  showing a false "0" (countFor already maps null → no count label). */
  urgentAlertCount: number | null;
  /** R5 Slice 3 (#507) — LIVE count of open Area Commands. Rendered INSIDE the row
   *  label ("Area Command (2)", incidentVocabulary.areaCommandLayerLabel) so the count
   *  reads even while the layer is OFF — the design gate's PEOC first-notice cue. */
  areaCommandCount: number;
  /** Stable Ontario Corps partner roster (name + category) for the
   *  category-grouped, expandable subtoggles. */
  ontarioCorpsPartners: ReadonlyArray<OntarioCorpsPartner>;
  /** #254 honest feed-health: layers whose backing daemon has gone dark, keyed by
   *  layer, plus the server clock the ages are judged against. Optional — absent
   *  (or an empty map) renders no down chips. */
  feedHealth?: { byLayer: Map<LayerKey, LayerFeedDown>; nowMs: number };
  /** #337 honest data-coverage: the road-closure feed's coverage state for the
   *  current view (derived from the map bbox + in-view closure count). Drives the
   *  per-view cue under the Road Closures row so an empty layer outside ON/AB/BC
   *  reads as "no feed here", not "no closures". Absent → 'unknown' (standing note
   *  only). */
  roadClosureCoverage?: RoadClosureCoverageState;
  /** When rendered inside the L.2.2 LeftRail tab, fill the rail (no own width /
   *  right border / "Layers" header — the rail provides those). */
  embedded?: boolean;
}

/** Category display order (matches the iconRegistry label insertion order). */
const OC_CATEGORY_ORDER = Object.keys(OC_CATEGORY_LABEL);

/** Small Lucide category glyph as an <img> (falls back to an amber dot). */
function CategoryGlyph({ category }: { category: string }): JSX.Element {
  const url = renderLucideToDataUrl(OC_CATEGORY_ICON[category] ?? 'circle', {
    color: OC_COLOR,
    size: 13,
  });
  if (!url) return <span className="inline-block w-3 h-3 rounded-full bg-amber-400" aria-hidden />;
  return <img src={url} width={13} height={13} alt="" aria-hidden />;
}

interface ToggleRowProps {
  label: string;
  swatchClass: string;
  checked: boolean;
  countLabel: string;
  onChange: (next: boolean) => void;
  title?: string;
  /** This row is an active class-level mute (#133) — show a "muted" chip. */
  muted?: boolean;
  /** #254 — this layer's backing feed is dark; render an honest "down since X"
   *  chip. `nowMs` is the server clock the age is judged against. */
  feedDown?: { downSinceMs: number; nowMs: number; workers: string[] };
}

function ToggleRow({
  label,
  swatchClass,
  checked,
  countLabel,
  onChange,
  title,
  muted,
  feedDown,
}: ToggleRowProps): JSX.Element {
  return (
    <label
      className="flex items-center gap-2 cursor-pointer py-1.5 px-2 rounded hover:bg-slate-800"
      title={title}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-sky-400"
      />
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${swatchClass}`} aria-hidden />
      <span className="text-sm flex-1">{label}</span>
      {feedDown && (
        <FeedDownChip
          downSinceMs={feedDown.downSinceMs}
          nowMs={feedDown.nowMs}
          workers={feedDown.workers}
        />
      )}
      {muted && <MutedChip />}
      <span className="text-[10px] text-slate-500 tabular-nums">{countLabel}</span>
    </label>
  );
}

/** Disabled "Coming in Phase X" placeholder row — scaffolded UI whose data ships
 *  in a later phase. Never toggles, never audits (L.1.5 #4); the title tooltip
 *  carries the phase. */
function PlaceholderRow({ layer }: { layer: LayerDef }): JSX.Element {
  const tip = `Coming in Phase ${layer.comingInPhase}`;
  return (
    <div
      className="flex items-center gap-2 py-1.5 px-2 rounded opacity-50 cursor-not-allowed select-none"
      title={tip}
      data-testid={`placeholder-${layer.label}`}
    >
      <input type="checkbox" disabled checked={false} readOnly className="accent-slate-600" />
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${layer.swatchClass}`} aria-hidden />
      <span className="text-sm flex-1 text-slate-400">{layer.label}</span>
      <span className="text-[9px] uppercase tracking-wide text-slate-500 bg-slate-800 rounded px-1 py-0.5">
        Phase {layer.comingInPhase}
      </span>
    </div>
  );
}

function count(on: boolean, n: number): string {
  return on ? n.toLocaleString() : '—';
}

/**
 * Threat Level map-fill KEY (icon-urgency council 2026-07-05, R1 — the founder-signed
 * reversal of the 2026-06-05 map-urgency lock; relabelled Threat by the #613 split,
 * founder §4.3 sign-off 2026-07-10). The AI-fused high-risk hotspot dots FILL by the
 * machine-derived 1–4 severity, so the operator needs a key for the four map fills.
 * Rendered under the Urgent Alerts row (when that layer is on).
 *
 * These fused-hotspot dots are RAW, un-owned hazards, so the axis is Threat Level, NOT
 * the §5 Response Level (an IC-assessed response scale on an owned incident). The
 * "AI-fused hotspots" sub-header makes the scope explicit so the multi-hazard surface is
 * not misrepresented. Swatches derive from the shared RESPONSE_LEVEL_COLORS palette so
 * the key can never drift. Non-interactive.
 */
function ThreatLevelKey(): JSX.Element {
  return (
    <div className="pl-8 pr-2 pb-1.5" data-testid="threat-level-key">
      <div className="text-[9px] uppercase tracking-wider text-slate-500 pb-0.5">
        AI-fused hotspots · dot fill
      </div>
      <div className="flex flex-col gap-0.5">
        {([1, 2, 3, 4] as const).map((lvl) => (
          <div
            key={lvl}
            className="flex items-center gap-2 text-[10px] text-slate-400"
            title={THREAT_LEVEL_META[lvl].criteria}
          >
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: RESPONSE_LEVEL_COLORS[lvl] }}
              aria-hidden
            />
            <span>{`Threat Level ${lvl} · ${THREAT_LEVEL_META[lvl].name}`}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function LayerToggle({
  prefs,
  onChange,
  cameraCount,
  sensorCount,
  assetCounts,
  aircraftCount,
  fireCount,
  urgentAlertCount,
  areaCommandCount,
  ontarioCorpsPartners,
  feedHealth,
  roadClosureCoverage,
  embedded = false,
}: LayerToggleProps): JSX.Element {
  const hidden = new Set(prefs.hiddenPartners);
  const hiddenRoles = new Set(prefs.hiddenAircraftRoles);
  const expanded = new Set(prefs.expandedCategories);

  // Collapsed group state (UI-only, not persisted). Seeded from each group's
  // defaultCollapsed: Default non-collapsible, Weather open, the rest collapsed.
  const [collapsed, setCollapsed] = useState<Set<GroupId>>(
    () => new Set(LAYER_GROUPS.filter((g) => g.defaultCollapsed).map((g) => g.id)),
  );
  const toggleGroupCollapsed = (id: GroupId): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // IMS audit: log every layer toggle with the COP context (the toggle states
  // BEFORE the flip, since `prefs` here is pre-change). Fire-and-forget.
  const logToggle = (layer: string, enabled: boolean): void => {
    logAction('toggle_layer', { layer, enabled }, { toggles: toggleSnapshot(prefs) });
  };

  /** Per-layer in-view count (null = no count, e.g. boundaries). */
  const countFor = (prefKey: BooleanPrefKey): number | null => {
    switch (prefKey) {
      case 'cameras':
        return cameraCount;
      case 'sensors':
        return sensorCount;
      case 'liveAircraft':
        return aircraftCount;
      case 'reportedOcWildfires':
      case 'allReportedWildfires':
        return fireCount;
      case 'urgentAnomalies':
        // #424 — all-reds urgent count (shared with the rail "Alerts" badge), not
        // the old fire-hotspots-only highRiskCount that omitted CAP/flood/ice reds.
        return urgentAlertCount;
      case 'fireStations':
        return assetCounts.fireStations;
      case 'wildfireBases':
        return assetCounts.wildfireBases;
      case 'aviationBases':
        return assetCounts.aviationBases;
      case 'ontarioCorps':
        return assetCounts.ontarioCorps;
      default:
        return null;
    }
  };

  /** Flip a real layer, applying radio-exclusivity within its group. */
  const setLayer = (group: LayerGroupDef, layer: LayerDef, enabled: boolean): void => {
    const prefKey = layer.prefKey as BooleanPrefKey;
    const next: LayerPrefs = { ...prefs, [prefKey]: enabled };
    if (enabled) {
      for (const sib of radioSiblings(group, layer)) (next[sib] as boolean) = false;
    }
    logToggle(layer.layerKey ?? prefKey, enabled);
    onChange(next);
  };

  // --- Ontario Corps partner sub-toggles (unchanged behaviour) ---------------
  const partnersByCategory = new Map<string, string[]>();
  for (const p of ontarioCorpsPartners) {
    const key = p.category ?? 'uncategorised';
    const list = partnersByCategory.get(key) ?? [];
    list.push(p.name);
    partnersByCategory.set(key, list);
  }
  const categories = [
    ...OC_CATEGORY_ORDER.filter((c) => partnersByCategory.has(c)),
    ...[...partnersByCategory.keys()].filter((c) => !OC_CATEGORY_ORDER.includes(c)),
  ];

  const togglePartner = (name: string, visible: boolean): void => {
    const next = new Set(hidden);
    if (visible) next.delete(name);
    else next.add(name);
    onChange({ ...prefs, hiddenPartners: [...next] });
  };
  const toggleCategory = (category: string, visible: boolean): void => {
    const members = partnersByCategory.get(category) ?? [];
    const next = new Set(hidden);
    for (const name of members) {
      if (visible) next.delete(name);
      else next.add(name);
    }
    onChange({ ...prefs, hiddenPartners: [...next] });
  };
  const toggleExpanded = (category: string): void => {
    const next = new Set(expanded);
    if (next.has(category)) next.delete(category);
    else next.add(category);
    onChange({ ...prefs, expandedCategories: [...next] });
  };
  const categoryVisible = (category: string): boolean => {
    const members = partnersByCategory.get(category) ?? [];
    return members.some((name) => !hidden.has(name));
  };
  const toggleRole = (role: string, visible: boolean): void => {
    const next = new Set(hiddenRoles);
    if (visible) next.delete(role);
    else next.add(role);
    onChange({ ...prefs, hiddenAircraftRoles: [...next] });
  };

  /** The special sub-section rendered beneath certain Default rows. */
  const renderSpecial = (layer: LayerDef): JSX.Element | null => {
    if (layer.special === 'cameras' && prefs.cameras) {
      return (
        <label
          className="flex items-center gap-2 cursor-pointer py-1 pl-7 pr-2 text-[11px] text-slate-300 rounded hover:bg-slate-800"
          title="Filter to cameras flagged for smoke-detection priority"
        >
          <input
            type="checkbox"
            checked={prefs.smokePriorityOnly}
            onChange={(e) => {
              logToggle('cameras-smoke-priority', e.target.checked);
              onChange({ ...prefs, smokePriorityOnly: e.target.checked });
            }}
            className="accent-amber-400"
          />
          <span>Smoke-priority only</span>
        </label>
      );
    }
    if (layer.special === 'roadClosures' && prefs.roadClosures) {
      // #337 honest data-coverage cue for the current view (null → standing note only).
      const coverageCue = roadClosureCoverageCue(roadClosureCoverage ?? 'unknown');
      return (
        <>
          <label
            className="flex items-center gap-2 cursor-pointer py-1 pl-7 pr-2 text-[11px] text-slate-300 rounded hover:bg-slate-800"
            title="Show lane/partial restriction dots (off = full closures only)"
          >
            <input
              type="checkbox"
              checked={prefs.roadClosuresRestrictions}
              onChange={(e) => {
                logToggle('ice-storm-road-closures-restrictions', e.target.checked);
                onChange({ ...prefs, roadClosuresRestrictions: e.target.checked });
              }}
              className="accent-amber-400"
            />
            <span className="flex-1">Restrictions</span>
            {!prefs.roadClosuresRestrictions && <MutedChip />}
          </label>
          {/* #337 honest data-coverage: the feed only reaches ON/AB/BC, so an empty
              layer elsewhere must not read as "no closures". A STANDING note scopes the
              feed; a per-view cue distinguishes "no feed here" from a covered province
              that genuinely has zero closures (unknown ≠ zero). Display-only. */}
          <div className="pl-7 pr-2 pb-1">
            <div
              data-testid="road-closure-coverage-note"
              className="text-[10px] text-slate-500 italic leading-snug"
            >
              {ROAD_CLOSURE_COVERAGE_NOTE}
            </div>
            {coverageCue && (
              <div
                data-testid="road-closure-coverage-cue"
                data-coverage={roadClosureCoverage}
                className={
                  roadClosureCoverage === 'no-feed'
                    ? 'mt-0.5 text-[10px] font-medium text-amber-300/90 leading-snug'
                    : 'mt-0.5 text-[10px] text-slate-400 leading-snug'
                }
              >
                {coverageCue}
              </div>
            )}
          </div>
        </>
      );
    }
    if (layer.special === 'communities' && layer.prefKey && prefs[layer.prefKey]) {
      // #234 (re-keying #189) — Vulnerability shading: ADDS the StatCan CIMD
      // 2021 Situational Vulnerability quintile to the community marker chip
      // (default OFF; contextual, PART IX).
      return (
        <label
          className="flex items-center gap-2 cursor-pointer py-1 pl-7 pr-2 text-[11px] text-slate-300 rounded hover:bg-slate-800"
          title="Tint the community chip by its CIMD 2021 (StatCan) Situational Vulnerability quintile (Q1 least – Q5 most deprived)"
        >
          <input
            type="checkbox"
            checked={prefs.communityVulnerabilityShading}
            onChange={(e) => {
              logToggle('community-vulnerability-shading', e.target.checked);
              onChange({ ...prefs, communityVulnerabilityShading: e.target.checked });
            }}
            className="accent-amber-400"
          />
          <span className="flex-1">Vulnerability shading</span>
        </label>
      );
    }
    if (layer.special === 'liveAircraft' && prefs.liveAircraft) {
      // #13 (FR24-style) — the per-role COLOUR legend is gone: the whole fleet
      // renders one gold, so a colour swatch here would key a code the map no
      // longer uses. These rows stay as a plain role-VISIBILITY filter (role
      // identity is read by clicking an aircraft, per #201) — no colour swatch.
      return (
        <div className="pl-5 pr-1">
          {AIRCRAFT_ROLES.map(({ role, label }) => (
            <label
              key={role}
              className="flex items-center gap-2 cursor-pointer py-0.5 pl-2 pr-2 text-[11px] text-slate-300 rounded hover:bg-slate-800"
              title={label}
            >
              <input
                type="checkbox"
                checked={!hiddenRoles.has(role)}
                onChange={(e) => toggleRole(role, e.target.checked)}
                className="accent-cyan-400"
              />
              <span className="truncate">{label}</span>
            </label>
          ))}
        </div>
      );
    }
    if (layer.special === 'ontarioCorps' && prefs.ontarioCorps) {
      return (
        <div className="pl-3 pr-1">
          <div className="text-[10px] text-amber-300/80 px-2 pb-1 italic">
            illustrative · {categories.length} categories
          </div>
          {categories.map((category) => {
            const members = partnersByCategory.get(category) ?? [];
            const isExpanded = expanded.has(category);
            const label = OC_CATEGORY_LABEL[category] ?? 'Uncategorised';
            return (
              <div key={category}>
                <div className="flex items-center gap-1 rounded hover:bg-slate-800">
                  <input
                    type="checkbox"
                    aria-label={label}
                    checked={categoryVisible(category)}
                    onChange={(e) => toggleCategory(category, e.target.checked)}
                    className="accent-amber-400 ml-2"
                  />
                  <button
                    type="button"
                    onClick={() => toggleExpanded(category)}
                    className="flex-1 text-left text-[11px] text-slate-200 px-1 py-1 flex items-center gap-1.5"
                    aria-expanded={isExpanded}
                  >
                    <span className="inline-block w-3 text-slate-500">{isExpanded ? '▾' : '▸'}</span>
                    <CategoryGlyph category={category} />
                    <span className="flex-1 truncate">{label}</span>
                    <span className="text-[10px] text-slate-500 tabular-nums">{members.length}</span>
                  </button>
                </div>
                {isExpanded && (
                  <div className="pl-8">
                    {members.map((name) => (
                      <label
                        key={name}
                        className="flex items-center gap-2 cursor-pointer py-0.5 pr-2 text-[11px] text-slate-300 rounded hover:bg-slate-800"
                        title={name}
                      >
                        <input
                          type="checkbox"
                          checked={!hidden.has(name)}
                          onChange={(e) => togglePartner(name, e.target.checked)}
                          className="accent-amber-400"
                        />
                        <span className="truncate">{name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const renderGroup = (group: LayerGroupDef): JSX.Element => {
    const isCollapsed = group.collapsible && collapsed.has(group.id);
    return (
      <section key={group.id} data-testid={`layer-group-${group.id}`} className="pt-2">
        {group.collapsible ? (
          <button
            type="button"
            onClick={() => toggleGroupCollapsed(group.id)}
            aria-expanded={!isCollapsed}
            className="w-full flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-300 hover:bg-slate-800 rounded"
          >
            <span className="inline-block w-3 text-slate-500">{isCollapsed ? '▸' : '▾'}</span>
            <span aria-hidden>{group.icon}</span>
            <span className="flex-1 text-left">{group.label}</span>
            {group.selection === 'radio' && (
              <span className="text-[9px] normal-case text-slate-500">one at a time</span>
            )}
          </button>
        ) : (
          <div className="px-2 py-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-300/90 border-b border-slate-700/60">
            <span aria-hidden>{group.icon}</span>
            <span className="flex-1">{group.label}</span>
          </div>
        )}
        {group.note && !isCollapsed && (
          <div className="px-2 pt-0.5 pb-1 text-[10px] text-slate-500 italic pl-6">{group.note}</div>
        )}
        {!isCollapsed && (
          <div>
            {group.layers.map((layer) => {
              if (layer.comingInPhase) return <PlaceholderRow key={layer.label} layer={layer} />;
              const prefKey = layer.prefKey as BooleanPrefKey;
              const on = Boolean(prefs[prefKey]);
              const n = countFor(prefKey);
              // R5 Slice 3 (#507) — the Area Command row carries its LIVE count in the
              // label itself (never the on-only right-side count), so a PEOC operator
              // sees "Area Command (2)" while the layer is still OFF (first-notice cue).
              const label =
                prefKey === 'areaCommand' && areaCommandCount > 0
                  ? areaCommandLayerLabel(areaCommandCount)
                  : layer.label;
              // EWW is a class-level mute (#133): OFF while the Anomaly Queue is ON
              // hides the CAP polygons but keeps the fused hotspots — flag it muted.
              const muted = prefKey === 'extremeWeatherWarnings' && prefs.urgentAnomalies && !on;
              // #254 — honest feed-health: if this layer's backing daemon is dark,
              // surface a "down since X" chip so a silently-stale layer never reads
              // as live.
              const fd = layer.layerKey ? feedHealth?.byLayer.get(layer.layerKey) : undefined;
              return (
                <div key={layer.layerKey ?? layer.label}>
                  <ToggleRow
                    label={label}
                    swatchClass={layer.swatchClass}
                    checked={on}
                    countLabel={n === null ? '' : count(on, n)}
                    title={layer.title}
                    muted={muted}
                    feedDown={
                      fd && feedHealth
                        ? {
                            downSinceMs: fd.downSinceMs,
                            nowMs: feedHealth.nowMs,
                            workers: fd.staleWorkers,
                          }
                        : undefined
                    }
                    onChange={(checked) => setLayer(group, layer, checked)}
                  />
                  {renderSpecial(layer)}
                  {/* icon-urgency council 2026-07-05 (R1): the fused-hotspot dots fill by
                      the machine-derived 1–4 severity — key the four map fills (Threat Level,
                      #613) under Urgent Alerts. */}
                  {layer.layerKey === 'urgent-anomalies' && on && <ThreatLevelKey />}
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  return (
    <aside
      data-testid="layer-toggle"
      className={
        embedded
          ? 'w-full h-full bg-slate-900/95 text-slate-100 flex flex-col overflow-y-auto'
          : 'w-52 border-r border-slate-700 bg-slate-900/95 text-slate-100 flex flex-col overflow-y-auto'
      }
    >
      {!embedded && (
        <header className="px-3 py-3 border-b border-slate-700">
          <h2 className="text-xs uppercase tracking-wider text-slate-400">Layers</h2>
        </header>
      )}
      <div className="p-1 space-y-0.5">{LAYER_GROUPS.map(renderGroup)}</div>
      <footer className="mt-auto px-3 py-3 border-t border-slate-700 text-[10px] text-slate-500 leading-relaxed">
        Ontario Corps is an <span className="text-amber-300/90">illustrative</span> layer — example
        locations pending a data partnership with MEPR.
      </footer>
    </aside>
  );
}
